import {
  HISTORY_FILTERS,
  ORDERS_PAGE_SIZE,
  SELL_HISTORY_FILTERS,
  type CryptoSellFilterId,
  type CryptoSwapFilterId,
} from "../constants/cryptoSwaps.constants";
import type { OtcSellOrdersQuery, OtcSellOrderStatus } from "../types/cryptoSell.types";
import type { OtcOrderStatus, OtcOrdersQuery } from "../types/cryptoSwaps.types";

/** Every filter is applied server-side, so the total and the export never describe a narrowed page. */
export interface OtcHistoryFilters<F extends string = string> {
  /** A chip id; `"all"` sends no status. */
  status: F;
  /** Days as `YYYY-MM-DD`, exactly what `ColocaDateRangeField` writes. */
  from: string;
  to: string;
  /** INTERNAL staff only. Empty = every organization. */
  organizationId: string;
}

export type CryptoSwapFiltersState = OtcHistoryFilters<CryptoSwapFilterId>;

export type CryptoSellFiltersState = OtcHistoryFilters<CryptoSellFilterId>;

export const DEFAULT_CRYPTO_SWAP_FILTERS: CryptoSwapFiltersState = {
  status: "all",
  from: "",
  to: "",
  organizationId: "",
};

export const DEFAULT_CRYPTO_SELL_FILTERS: CryptoSellFiltersState = {
  status: "all",
  from: "",
  to: "",
  organizationId: "",
};

export function hasActiveFilters(filters: OtcHistoryFilters): boolean {
  return (
    filters.status !== "all" || filters.from !== "" || filters.to !== "" || filters.organizationId !== ""
  );
}

/** Widened to UTC day boundaries so "1 Sep – 23 Sep" includes everything on the 23rd. */
export function dayRangeToInstants(from: string, to: string): { from?: string; to?: string } {
  return {
    from: from ? `${from}T00:00:00.000Z` : undefined,
    to: to ? `${to}T23:59:59.999Z` : undefined,
  };
}

export function statusForFilter(id: CryptoSwapFilterId): OtcOrderStatus | null {
  return HISTORY_FILTERS.find((filter) => filter.id === id)?.status ?? null;
}

export function sellStatusForFilter(id: CryptoSellFilterId): OtcSellOrderStatus | null {
  return SELL_HISTORY_FILTERS.find((filter) => filter.id === id)?.status ?? null;
}

function buildHistoryQuery<F extends string>(
  filters: OtcHistoryFilters<F>,
  page: number,
  pageSize: number,
  status: string | null,
) {
  const range = dayRangeToInstants(filters.from, filters.to);
  return {
    skip: (page - 1) * pageSize,
    limit: pageSize,
    ...(status ? { status } : {}),
    ...(range.from ? { from: range.from } : {}),
    ...(range.to ? { to: range.to } : {}),
    ...(filters.organizationId ? { organization_id: filters.organizationId } : {}),
  };
}

export function buildOrdersQuery(
  filters: CryptoSwapFiltersState,
  page: number,
  pageSize: number = ORDERS_PAGE_SIZE,
): OtcOrdersQuery {
  return buildHistoryQuery(filters, page, pageSize, statusForFilter(filters.status));
}

export function buildSellOrdersQuery(
  filters: CryptoSellFiltersState,
  page: number,
  pageSize: number = ORDERS_PAGE_SIZE,
): OtcSellOrdersQuery {
  return buildHistoryQuery(filters, page, pageSize, sellStatusForFilter(filters.status));
}

/** Narrowing to one match while on page 4 must not render an empty table over a non-zero total. */
export function clampPage(page: number, total: number, pageSize: number = ORDERS_PAGE_SIZE): number {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  return Math.min(Math.max(1, page), lastPage);
}
